<script>
import { generateTimeSeriesData } from "./reporting-mock-data";
import toCurrency from "@/helpers/currency";
import moment from "moment";

export default {
  name: "reporting-data-table",
  props: {
    periodType: {
      type: String,
      default: "month",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentData: [],
      previousData: [],
      fields: [
        {
          key: "period",
          label: "Period",
          sortable: true,
          thClass: "text-center",
          tdClass: "text-center font-weight-bold",
        },
        {
          key: "revenue",
          label: "Revenue",
          sortable: true,
          thClass: "text-end",
          tdClass: "text-end",
          formatter: (value) => toCurrency(value),
        },
        {
          key: "quantity",
          label: "Quantity",
          sortable: true,
          thClass: "text-end",
          tdClass: "text-end",
          formatter: (value) => Math.round(value).toLocaleString(),
        },
        {
          key: "revenueDelta",
          label: "Δ Revenue",
          sortable: true,
          thClass: "text-end",
          tdClass: "text-end",
        },
        {
          key: "revenuePercent",
          label: "Δ Revenue %",
          sortable: true,
          thClass: "text-end",
          tdClass: "text-end",
        },
        {
          key: "quantityDelta",
          label: "Δ Quantity",
          sortable: true,
          thClass: "text-end",
          tdClass: "text-end",
        },
        {
          key: "quantityPercent",
          label: "Δ Quantity %",
          sortable: true,
          thClass: "text-end",
          tdClass: "text-end",
        },
      ],
      tableData: [],
      currentPage: 1,
      perPage: 10,
      sortBy: "period",
      sortDesc: true,
    };
  },
  computed: {
    totalRows() {
      return this.tableData.length;
    },
  },
  watch: {
    periodType: {
      handler: "loadData",
      immediate: true,
    },
  },
  methods: {
    loadData() {
      // Simulate API call with mock data
      const currentYear = moment().year();
      this.currentData = generateTimeSeriesData(this.periodType, currentYear);
      this.previousData = generateTimeSeriesData(
        this.periodType,
        currentYear - 1
      );
      this.processTableData();
    },
    processTableData() {
      this.tableData = this.currentData.map((current, index) => {
        const previous = this.previousData[index] || {
          revenue: 0,
          quantity: 0,
        };

        const revenueDelta = current.revenue - previous.revenue;
        const revenuePercent =
          previous.revenue > 0
            ? (current.revenue / previous.revenue - 1) * 100
            : 0;

        const quantityDelta = current.quantity - previous.quantity;
        const quantityPercent =
          previous.quantity > 0
            ? (current.quantity / previous.quantity - 1) * 100
            : 0;

        return {
          period: this.formatPeriodLabel(current.period),
          revenue: current.revenue,
          quantity: current.quantity,
          revenueDelta: revenueDelta,
          revenuePercent: revenuePercent,
          quantityDelta: quantityDelta,
          quantityPercent: quantityPercent,
        };
      });
    },
    formatPeriodLabel(period) {
      switch (this.periodType) {
        case "week":
          return period; // Format: YYYY-WWW
        case "month":
          return moment(period, "YYYY-MM").format("MMM YYYY");
        case "year":
          return period;
        default:
          return period;
      }
    },
    getDeltaClass(value) {
      if (value > 0) return "text-success";
      if (value < 0) return "text-danger";
      return "text-muted";
    },
    formatDelta(value) {
      const sign = value > 0 ? "+" : "";
      return sign + toCurrency(value);
    },
    formatQuantityDelta(value) {
      const sign = value > 0 ? "+" : "";
      return sign + Math.round(value).toLocaleString();
    },
    formatPercent(value) {
      const sign = value > 0 ? "+" : "";
      return sign + value.toFixed(2) + "%";
    },
  },
};
</script>

<template>
  <b-card no-body>
    <b-card-body>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="card-title mb-0">Detailed Period Comparison</h4>
        <div>
          <b-button variant="light" size="sm" @click="loadData">
            <i class="bx bx-refresh"></i> Refresh
          </b-button>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="text-muted mt-2">Loading table data...</p>
      </div>

      <div v-else>
        <!-- Table -->
        <b-table
          :items="tableData"
          :fields="fields"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          responsive
          striped
          hover
          class="mb-0"
        >
          <!-- Custom rendering for delta columns -->
          <template #cell(revenueDelta)="data">
            <span :class="getDeltaClass(data.value)">
              <i
                v-if="data.value !== 0"
                class="bx align-middle me-1"
                :class="
                  data.value > 0 ? 'bx-up-arrow-alt' : 'bx-down-arrow-alt'
                "
              ></i>
              {{ formatDelta(data.value) }}
            </span>
          </template>

          <template #cell(revenuePercent)="data">
            <span :class="getDeltaClass(data.value)" class="font-weight-bold">
              {{ formatPercent(data.value) }}
            </span>
          </template>

          <template #cell(quantityDelta)="data">
            <span :class="getDeltaClass(data.value)">
              <i
                v-if="data.value !== 0"
                class="bx align-middle me-1"
                :class="
                  data.value > 0 ? 'bx-up-arrow-alt' : 'bx-down-arrow-alt'
                "
              ></i>
              {{ formatQuantityDelta(data.value) }}
            </span>
          </template>

          <template #cell(quantityPercent)="data">
            <span :class="getDeltaClass(data.value)" class="font-weight-bold">
              {{ formatPercent(data.value) }}
            </span>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <div class="text-muted">
            Showing {{ (currentPage - 1) * perPage + 1 }} to
            {{ Math.min(currentPage * perPage, totalRows) }} of
            {{ totalRows }} entries
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="right"
            size="sm"
            class="mb-0"
          ></b-pagination>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<style scoped>
.card-title {
  font-size: 1.1rem;
  font-weight: 600;
}

::v-deep .table th {
  background-color: #f8f9fa;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

::v-deep .table td {
  vertical-align: middle;
  font-size: 0.875rem;
}
</style>
